/* eslint-disable vue/one-component-per-file */
import type { PropType } from 'vue';
import { onMounted, ref, defineComponent } from 'vue';
import {
  VTextField,
  VRadioGroup,
  VTextarea,
  VRadio,
  VFileInput,
  VCombobox,
  VOtpInput,
} from 'vuetify/components';
import type { TestIdInput } from '@/common/components/ActionableComponent';
import {
  attrName,
  getTestIdString,
  getModelExpression,
} from '@/common/components/ActionableComponent';
import FormField, {
  useValue,
  getAttributes,
  getOnBlur,
  formFieldProps,
} from './FormField.vue';
import ImprovedVAutocomplete from './ImprovedVAutocomplete.vue';

export const SearchAndSelectField = defineComponent({
  extends: FormField,
  props: formFieldProps,
  setup(props, { emit }) {
    const internalValue = useValue(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'SearchAndSelectField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return ImprovedVAutocomplete;
    },
  },
});

export const ComboboxField = defineComponent({
  extends: FormField,
  props: formFieldProps,
  setup(props, { emit }) {
    const internalValue = useValue(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'ComboboxField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VCombobox;
    },
  },
});

export const TextField = defineComponent({
  extends: FormField,
  props: { ...formFieldProps, modelValue: String },
  setup(props, { emit }) {
    const internalValue = useValue<string>(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'TextField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VTextField;
    },
  },
});

export const NumberField = defineComponent({
  extends: FormField,
  props: {
    ...formFieldProps,
    modelValue: Number,
    type: {
      type: String as PropType<'number'>,
      default: 'number',
    },
  },
  setup(props, { emit }) {
    const converter = (input: unknown) =>
      input === '' ? undefined : Number(input);
    const internalValue = useValue<number>(props, emit, converter);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'NumberField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VTextField;
    },
  },
});

const setTestIdRadioGroup = (el: Element, id: string) => {
  el.querySelectorAll('input[type="radio"]').forEach(input => {
    input.setAttribute(attrName, `${id}-value-${input.getAttribute('value')}`);
  });
};
export const RadioGroup = defineComponent({
  directives: {
    setTestId: {
      created(el, { value }) {
        const id = getTestIdString(value as TestIdInput);
        setTestIdRadioGroup(el, id);
      },
      updated(el, { value }) {
        const id = getTestIdString(value as TestIdInput);
        setTestIdRadioGroup(el, id);
      },
    },
  },
  extends: FormField,
  props: {
    ...formFieldProps,
    direction: {
      type: String as PropType<'horizontal' | 'vertical'>,
      default: 'vertical',
    },
  },
  setup(props, { emit }) {
    const internalValue = useValue(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'RadioGroup';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VRadioGroup;
    },
  },
});

export const TextareaField = defineComponent({
  extends: FormField,
  props: { ...formFieldProps, modelValue: String },
  setup(props, { emit }) {
    const internalValue = useValue<string>(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'TextareaField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VTextarea;
    },
    testIdSelector() {
      return 'textarea';
    },
  },
});

export const RadioField = defineComponent({
  directives: {
    setTestId: {},
  },
  extends: FormField,
  props: formFieldProps,
  setup(props, { emit }) {
    const internalValue = useValue(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'RadioField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VRadio;
    },
  },
});

export const FileField = defineComponent({
  extends: FormField,
  props: {
    ...formFieldProps,
    modelValue: [Array, Object] as PropType<File | File[]>,
  },
  setup(props, { emit }) {
    const internalValue = useValue<File | File[]>(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'FileField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return VFileInput;
    },
  },
});

export const OtpField = defineComponent({
  extends: FormField,
  props: {
    ...formFieldProps,
    length: { type: Number, default: 6 },
    modelValue: String,
    autofocus: Boolean,
  },
  setup(props, { emit }) {
    const converter = (input: unknown) =>
      typeof input === 'string'
        ? input.trim().substring(0, props.length)
        : undefined;
    const internalValue = useValue<string>(props, emit, converter);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'OtpField';

    // remove this when VOtpInput fixes the broken autofocus functionality
    const el = ref<typeof VOtpInput>();
    onMounted(() => {
      if (!props.autofocus) {
        return;
      }
      if (!el.value?.$el) {
        throw new Error('otp field $el not found');
      }
      (el.value.$el as Element)?.querySelector('input')?.focus();
    });

    return {
      internalValue,
      attrs,
      onBlur,
      componentName,
      getModelExpression,
      el,
    };
  },
  computed: {
    fieldComponent() {
      return VOtpInput;
    },
  },
});
